/**
 * Legal Rights and Information (India-specific)
 */

export const LEGAL_RIGHTS = {
  pdiRights: {
    title: 'Your Right to PDI',
    content: `As a car buyer in India, you have the absolute right to conduct a Pre-Delivery Inspection before accepting your vehicle. This is your legal right, not a favor from the dealer.`,
    points: [
      'PDI is your right under the Consumer Protection Act, 2019',
      'You can refuse delivery if the vehicle doesn\'t meet expected standards',
      'Dealers cannot force you to sign delivery documents before PDI',
      'You can bring your own mechanic for inspection',
      'You can take photos and videos during PDI',
    ],
  },
  
  consumerProtection: {
    title: 'Consumer Protection Act, 2019',
    content: `The Consumer Protection Act provides strong protections for car buyers:`,
    points: [
      'Right to be informed about goods and services',
      'Right to seek redressal against unfair practices',
      'Right to consumer awareness',
      'Protection against defective goods',
      'Right to compensation for defects and deficiency in service',
    ],
    relevantSections: [
      {
        section: 'Section 2(6)',
        description: 'Defines "defect" in goods including manufacturing defects',
      },
      {
        section: 'Section 2(11)',
        description: 'Defines "deficiency" in service',
      },
      {
        section: 'Section 35',
        description: 'Jurisdiction of Consumer Commissions',
      },
    ],
  },
  
  dealerRefusal: {
    title: 'If Dealer Refuses PDI',
    steps: [
      {
        step: 1,
        action: 'Request in writing',
        description: 'Ask for written reason why PDI is being denied',
      },
      {
        step: 2,
        action: 'Escalate to Dealer Principal',
        description: 'Request to speak with the dealership owner/principal',
      },
      {
        step: 3,
        action: 'Contact manufacturer',
        description: 'Call manufacturer\'s customer care and lodge complaint',
      },
      {
        step: 4,
        action: 'Social media',
        description: 'Tag manufacturer on Twitter/X with your concern',
      },
      {
        step: 5,
        action: 'Consumer forum',
        description: 'File complaint with District Consumer Forum',
      },
    ],
  },
  
  escalationContacts: {
    title: 'Escalation Contacts',
    contacts: [
      {
        name: 'National Consumer Helpline',
        number: '1800-11-4000',
        type: 'Toll-free',
      },
      {
        name: 'Consumer Online Resource (CORE)',
        website: 'consumerhelpline.gov.in',
        type: 'Online Portal',
      },
      {
        name: 'SIAM (Society of Indian Automobile Manufacturers)',
        email: 'grievance@siam.in',
        type: 'Industry Body',
      },
    ],
  },
  
  sampleEscalationEmail: {
    title: 'Sample Escalation Email',
    subject: 'Complaint Regarding [Dealer Name] - [City] - Delivery Issues',
    template: `To,
The Customer Care Team,
[Manufacturer Name]

Subject: Formal Complaint Against [Dealer Name], [City]

Dear Sir/Madam,

I am writing to bring to your attention serious concerns regarding my vehicle delivery experience at [Dealer Name], [Location].

**Booking Details:**
- Booking Date: [Date]
- Vehicle Model: [Model Name]
- Variant: [Variant]
- Color: [Color]
- Booking Amount Paid: ₹[Amount]
- Invoice Number: [If available]

**Issue Description:**
During my Pre-Delivery Inspection on [Date], I observed the following issues:

1. [Issue 1 with details]
2. [Issue 2 with details]
3. [Issue 3 with details]

**Dealer's Response:**
The dealership staff [describe their response/excuse]

**My Request:**
I request immediate intervention to:
1. [Specific resolution requested]
2. [Timeline expected]

I have documented all issues with photographs and am prepared to share them as evidence.

I trust that [Manufacturer Name] values customer satisfaction and will take appropriate action against such practices.

Please acknowledge receipt of this complaint and provide a resolution timeline within 48 hours.

Regards,
[Your Name]
[Phone Number]
[Email]
[City]

CC:
- Regional Manager
- District Consumer Forum
- [Relevant social media handles]`,
  },
  
  documentChecklist: {
    title: 'Documents You Must Receive',
    mandatory: [
      {
        document: 'Tax Invoice',
        importance: 'Primary proof of purchase',
        verify: 'Check all amounts, model, color, accessories match',
      },
      {
        document: 'Form 21',
        importance: 'Sale certificate from manufacturer',
        verify: 'Required for registration',
      },
      {
        document: 'Form 22',
        importance: 'Road-worthiness certificate',
        verify: 'Must be present for registration',
      },
      {
        document: 'Insurance Policy',
        importance: 'Mandatory for driving',
        verify: 'Check IDV, coverage, start date',
      },
      {
        document: 'Temporary Registration',
        importance: 'Allows legal driving before RC',
        verify: 'Valid for 30 days typically',
      },
      {
        document: 'Warranty Card',
        importance: 'Proof of warranty coverage',
        verify: 'VIN, purchase date must be filled',
      },
      {
        document: 'Service Booklet',
        importance: 'Service schedule and records',
        verify: 'First free service details',
      },
      {
        document: 'Owner\'s Manual',
        importance: 'Operating instructions',
        verify: 'Can be digital or physical',
      },
    ],
    optional: [
      'Extended Warranty Documents',
      'Road-side Assistance Card',
      'Accessory Installation Invoice',
      'Paint Protection Certificate',
      'Underbody Coating Certificate',
    ],
  },
  
  warranty: {
    title: 'Understanding Your Warranty',
    points: [
      'Standard warranty typically 2-3 years or distance-based',
      'Warranty starts from date of invoice, not registration',
      'Battery usually has separate shorter warranty',
      'Extended warranty can be purchased within standard warranty period',
      'Warranty is tied to VIN, not owner - transfers with car',
    ],
    warningActions: [
      'Unauthorized modifications',
      'Skipping scheduled services',
      'Using non-genuine parts',
      'Racing or rallying',
      'Commercial use if bought as personal',
    ],
  },
};

/**
 * Get all legal sections
 */
export const getAllLegalSections = () => {
  return Object.entries(LEGAL_RIGHTS).map(([key, value]) => ({
    id: key,
    ...value,
  }));
};

export default LEGAL_RIGHTS;
